/* GridRowComponent.js */

"use strict";

const _ = require('lodash');
const EventEmitter = require('events').EventEmitter;

class GridRowComponent extends EventEmitter {

    constructor(configOptions) {
        super();

        this.setMaxListeners(0);

        this._id = _.get(configOptions, 'id');
        this._entity = _.get(configOptions, 'entity');
        this._columnOrder = _.get(configOptions, 'columnOrder');
        this._lockedColumns = _.get(configOptions, 'lockedColumns');
        this._rightLockedColumns = _.get(configOptions, 'rightLockedColumns');
        this._columnComponents = _.get(configOptions, 'columnComponents');
        this._selected = _.get(configOptions, 'selected');
        this._top = _.get(configOptions, 'top');
        this._pageIndex = _.get(configOptions, 'pageIndex');
        this._even = _.get(configOptions, 'even');
        this._grid = _.get(configOptions, 'grid');
        this._allowDragNDrop = _.get(configOptions, 'allowDragNDrop');

        this._active = false;

        this._timeoutDuration = 50;

        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/
    _registerListeners() {
        this._registerEntityListeners();
    }

    _unregisterListeners() {
        this._unregisterEntityListeners();
    }

    _registerEntityListeners() {
        if (this._entity) {
            this._entity.on('PropertiesUpdated', this._onEntityPropertiesUpdated = this._onEntityPropertiesUpdated.bind(this));
            this._entity.on('UpdatedByChanged', this._onEntityUpdatedByChanged = this._onEntityUpdatedByChanged.bind(this));
            this._entity.on('UpdatedOnChanged', this._onEntityUpdatedOnChanged = this._onEntityUpdatedOnChanged.bind(this));
        }
    }

    _unregisterEntityListeners() {
        if (this._entity) {
            this._entity.removeListener('PropertiesUpdated', this._onEntityPropertiesUpdated);
            this._entity.removeListener('UpdatedByChanged', this._onEntityUpdatedByChanged);
            this._entity.removeListener('UpdatedOnChanged', this._onEntityUpdatedOnChanged);
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/
    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(() => {
            this._renderer = undefined;
            this._id = undefined;
            this._entity = undefined;
            this._columnOrder = undefined;
            this._lockedColumns = undefined;
            this._columnComponents = undefined;
            this._selected = undefined;
            this._top = undefined;
            this._active = undefined;
            this._timeoutDuration = undefined;
            this._pageIndex = undefined;
            this._even = undefined;
        });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /**
     * @private Fired when this Entity's properties have been updated in the system.
     * @param {object} newProperties - The properties after the update.
     * @param {object} oldProperties - The properties before the update.
     * @param {JsonPatch|null} pendingPatches - The current pending patches. When the properties are updated
     * from the system the pending changes are rebased and if there are still differences the entity will stay
     * in a dirty state and the pending changes will now reflect only what is different between this entity and
     * the pristine version in the system.
     */
    _onEntityPropertiesUpdated(newProperties /*, oldProperties, pendingPatches*/) {
        this._updateEntityProperties(newProperties);
    }

    _onEntityUpdatedByChanged(newProperties /*, oldProperties, pendingPatches*/) {
        this._updateEntityProperties({updatedBy: newProperties});
    }

    _onEntityUpdatedOnChanged(newProperties /*, oldProperties, pendingPatches*/) {
        this._updateEntityProperties({updatedOn: newProperties});
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _triggerRowUserSelected(event) {
        this.emit('RowUserSelected', this._rowData, event);
    }

    _triggerRowUserActive(event) {
        this.emit('RowUserActive', {
            row: this.getId(),
            active: this.getActive(),
            event: event
        });
    }

    _triggerRowUserDoubleClicked(event) {
        this.emit('RowUserDoubleClicked', this._rowData, event);
    }

    _triggerRowAction(eventData) {
        let rowEvent = {
            rowId: this.getId(),
            eventData: eventData
        };
        this.emit('RowAction', rowEvent);
    }

    _updateEntityProperties(newProperties) {
        this.emit('UpdateEntityProperties', newProperties);
        return this;
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    setId(newVal) {
        if (newVal !== this._id) {
            this._id = newVal;
            this.emit('ID', this._id);
        }
        return this;
    }

    getId() {
        return this._id;
    }

    setEntity(newVal) {
        if (newVal !== this._entity) {
            this._unregisterEntityListeners();
            this._entity = newVal;
            this._registerEntityListeners();
            this.emit('Entity', this._entity);
        }
        return this;
    }

    getEntity() {
        return this._entity;
    }

    /**
     * Set the column order in the row
     * @param newVal {Array} - An array of strings of column keys representing the column order in the grid
     * @param noEmit {Boolean} - If true, set the data without emitting the event
     * @returns {GridRowComponent}
     */
    setColumnOrder(newVal, noEmit) {
        if (newVal !== this._columnOrder) {
            this._columnOrder = newVal;
            if (!noEmit) {
                this.emit('ColumnOrder', this._columnOrder);
            }
        }
        return this;
    }

    getColumnOrder() {
        return this._columnOrder;
    }

    /**
     * Set the column order in the row
     * @param newVal {Array} - An array of strings of column keys representing the order of locked columns
     * @param noEmit {Boolean} - If true, set the data without emitting the event
     * @returns {GridRowComponent}
     */
    setLockedColumns(newVal, noEmit) {
        this._lockedColumns = newVal;
        if (!noEmit) {
            this.emit('LockedColumns', this._lockedColumns);
        }
        return this;
    }

    getLockedColumns() {
        return this._lockedColumns;
    }

    setColumnComponents(newVal, noEmit) {
        if (newVal !== this._columnComponents) {
            this._columnComponents = newVal;

            // it would be better to generate locked columns from scratch, but it can be done only from EntityGridComponent
            for (let i = 0; i < this._lockedColumns.length; i++) {
                let colId = this._lockedColumns[i];
                if (!newVal[colId]) {
                    this._lockedColumns.splice(i--, 1);
                }
            }
            if (!noEmit) {
                this.emit('ColumnComponents', this._columnComponents);
            }
        }
        return this;
    }

    getColumnComponents() {
        return this._columnComponents;
    }

    setSelected(newVal) {
        if (newVal !== this._selected) {
            this._selected = newVal;
            this.emit('Selected', this._selected);
        }
        return this;
    }

    getSelected() {
        return this._selected;
    }

    setTop(newVal) {
        if (newVal !== this._top) {
            this._top = newVal;
            this.emit('Top', this._top);
        }
        return this;
    }

    getTop() {
        return this._top;
    }

    setActive(newVal) {
        if (newVal !== this._active) {
            this._active = newVal;
            this.emit('Active', this._active);
        }
        return this;
    }

    getActive() {
        return this._active;
    }

    getTimeoutDuration() {
        return this._timeoutDuration;
    }

    setPageIndex(newVal) {
        if (newVal !== this._pageIndex) {
            this._pageIndex = newVal;
            // this.emit('PageIndex', this._pageIndex);
        }
        return this;
    }

    getPageIndex() {
        return this._pageIndex;
    }

    setEven(newVal) {
        if (newVal !== this._even) {
            this._even = newVal;
            this.emit('Even', this._even);
        }
        return this;
    }

    getEven() {
        return this._even;
    }

    /**
     * NOTE: USED IN PLACE OF A COLUMN EVENT BECAUSE UNBINDING COLUMN EVENTS AT THE ROW
     * LEVEL KILLS PERFORMANCE DURING PAGINATION.
     * Triggered by the 'UserResized' event for each column.
     * @param columnData.column {String} - The id of the column that has been resized
     * @param columnData.width {Number} - The width in pixels of the column
     * @param columnData.event {Event} - The mouse event that triggered the resize
     * @private
     */
    columnWidthUpdate(columnData) {
        this.emit('ColumnWidth', columnData);
        return this;
    }

    getGrid(){
        return this._grid;
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = GridRowComponent;